import React, { forwardRef, Ref } from 'react'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { StyledClickable } from '../StyledClickable/StyledClickable'
import { LinkAction } from './LinkAction'
import { isActionRef, isHrefRef } from './helpers/typeguards'
import { ProjectLinkProps } from './helpers/types'

export const Link = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ProjectLinkProps
>(
  (
    {
      action,
      children,
      download,
      size,
      appearance = 'text',
      href,
      tabIndex,
      target,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      className,
      label,
      title,
    },
    ref,
  ) => {
    const router = useRouter()

    // Check if href is a same-page anchor link
    let isSamePageAnchor = false
    let anchorId: string | null = null

    if (href && typeof href === 'string') {
      const anchorMatch = href.match(/#(.+)$/)
      if (anchorMatch) {
        const [pathname] = href.split('#')
        anchorId = anchorMatch[1]

        // Check if the page URL matches current route
        isSamePageAnchor =
          pathname === router.pathname ||
          pathname === router.asPath.split('#')[0]
      }
    }

    // Handle smooth scrolling for same-page anchors
    const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isSamePageAnchor && anchorId) {
        e.preventDefault()
        const element = document.getElementById(anchorId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
      onClick?.(e)
    }

    const elementProps = {
      download,
      onClick: isSamePageAnchor && anchorId ? handleAnchorClick : onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      tabIndex,
      target,
    }

    const linkContent = (
      <StyledClickable
        ref={ref as Ref<HTMLAnchorElement>}
        as={'a'}
        className={className}
        appearance={appearance}
        size={size}
        title={title}
        aria-label={label}
        {...elementProps}
      >
        {children}
      </StyledClickable>
    )

    if (href) {
      // For same-page anchors, use regular anchor tag with programmatic smooth scrolling
      if (isSamePageAnchor && anchorId) {
        return (
          <StyledClickable
            as={'a'}
            href={`#${anchorId}`}
            className={className}
            appearance={appearance}
            size={size}
            title={title}
            aria-label={label}
            ref={isHrefRef(ref, { href }) ? ref : undefined}
            {...elementProps}
          >
            {children}
          </StyledClickable>
        )
      }

      // For cross-page links (including anchors), use Next.js Link
      return (
        <NextLink
          href={href}
          passHref
          title={title}
          ref={isHrefRef(ref, { href }) ? ref : undefined}
          legacyBehavior
        >
          {linkContent}
        </NextLink>
      )
    }

    if (action) {
      return (
        <LinkAction
          action={action}
          className={className}
          renderTrigger={(clickAction) => (
            <StyledClickable
              ref={isActionRef(ref, { action }) ? ref : undefined}
              as={'button'}
              className={className}
              appearance={appearance}
              size={size}
              title={title}
              aria-label={label}
              {...elementProps}
              onClick={clickAction.click}
            >
              {children}
            </StyledClickable>
          )}
        />
      )
    }

    return null
  },
)
